import { OverlayLoader } from 'components/Loaders'
import { actions, selectors } from 'data'
import { Bank, BankState, YapilyInstitution } from 'data/Payments/BankTransfer/types'
import format from 'date-fns/format'
import React, { useEffect, useState } from 'react'
import { connect, ConnectedProps } from 'react-redux'

import GeneralError from '../GeneralError'
import Agreement from './Agreement'
import ConnectBank from './ConnectBank'
import SelectInstitution from './SelectInstitution'

const A_DAY = 1000 * 60 * 60 * 24
const getAgreementExpireDate = () => format(+new Date() + A_DAY * 90, 'MMM DD YYYY')

type OwnProps = {
  // eslint-disable-next-line react/no-unused-prop-types
  id: string // used in mapStatToProps
  institutions: YapilyInstitution[]
  onBack: () => void
  onSuccess: (bank: Bank) => void
}
type Props = OwnProps & ConnectedProps<typeof connector>

const FETCH_INTERVAL = 3000

type Step = 'institution' | 'agreement' | 'polling'
const Yapily = ({
  bank,
  bankError,
  fetchBank,
  institutions,
  onBack,
  onSuccess,
  updateBank
}: Props) => {
  const [step, setStep] = useState<Step>('institution')
  const [institution, setInstitution] = useState<YapilyInstitution | undefined>(undefined)
  useEffect(fetchBank, [])
  useEffect(
    function pollBankEffect() {
      if (step !== 'polling') return
      const interval = setInterval(fetchBank, FETCH_INTERVAL)
      return () => clearInterval(interval)
    },
    [step]
  )
  useEffect(
    function successEffect() {
      if (bank?.state === BankState.ACTIVE) onSuccess(bank)
    },
    [bank]
  )

  if (bankError && !bank) {
    return <GeneralError onBack={onBack} />
  }

  if (!bank || bank.partner !== 'YAPILY') {
    return <OverlayLoader />
  }

  if (bank.state === BankState.BLOCKED) {
    return <GeneralError error={bank.error} onBack={onBack} />
  }

  switch (step) {
    case 'institution':
      return (
        <SelectInstitution
          institutions={institutions}
          onBack={onBack}
          onSelect={(selectedInstitution) => {
            setStep('agreement')
            setInstitution(selectedInstitution)
          }}
        />
      )
    case 'agreement': {
      return (
        <Agreement
          entity={bank.attributes!.entity}
          expireDate={getAgreementExpireDate()}
          onAccept={() => {
            setStep('polling')
            updateBank(institution!.id)
          }}
          onDecline={() => {
            setStep('institution')
          }}
        />
      )
    }
    case 'polling': {
      if (!bank || bank.partner !== 'YAPILY' || !bank?.attributes?.authorisationUrl) {
        return <OverlayLoader />
      }

      return (
        <ConnectBank
          institutionImg={institution!.media[0].source}
          linkUrl={bank.attributes.authorisationUrl}
          onBack={onBack}
          qrImg={bank.attributes.qrcodeUrl}
          title='link_bank_connect_bank_title'
        />
      )
    }

    default:
      return null
  }
}

const mapStateToProps = (state, { id }: OwnProps) => ({
  bank: selectors.payments.bankTransfer.getBank(state)(id),
  bankError: selectors.payments.bankTransfer.getBankError(state)(id)
})

const mapDispatchToProps = (dispatch, { id }: OwnProps) => ({
  fetchBank: () => {
    dispatch(actions.payments.fetchBank(id))
  },
  updateBank: (institutionId) => {
    dispatch(actions.payments.updateBankLink(id, { institutionId }))
  }
})

const connector = connect(mapStateToProps, mapDispatchToProps)
export default connector(Yapily)
